import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import SimWebsiteBuilder from "./sim/SimWebsiteBuilder";
import SimPackageBuilder from "./sim/SimPackageBuilder";
import SimPricingPublish from "./sim/SimPricingPublish";
import SimCustomerInquiry from "./sim/SimCustomerInquiry";
import SimInbox from "./sim/SimInbox";
import SimQuote from "./sim/SimQuote";
import { scrollToId } from "./Reveal";

const STEPS = [
  { title: "Brand your website", Sim: SimWebsiteBuilder },
  { title: "Build a package", Sim: SimPackageBuilder },
  { title: "Price & publish", Sim: SimPricingPublish },
  { title: "Customer sends an inquiry", Sim: SimCustomerInquiry },
  { title: "Open it in the dashboard", Sim: SimInbox },
  { title: "Create the quotation", Sim: SimQuote },
];

export default function DemoMode() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const last = step === STEPS.length;

  useEffect(() => {
    const onOpen = () => { setOpen(true); setStep(0); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("tt:open-demo", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("tt:open-demo", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const Step = STEPS[step]?.Sim;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex flex-col bg-paper"
          data-testid="demo-mode-overlay"
          data-lenis-prevent
        >
          <div className="flex items-center justify-between border-b border-sand px-5 py-3.5 sm:px-8">
            <p className="font-serif text-xl text-ink">Travel-Tek<span className="text-terra">.</span>
              <span className="ml-3 rounded-full bg-terra/15 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-terra">Interactive demo · sample data</span>
            </p>
            <button onClick={() => setOpen(false)} data-testid="demo-mode-close-btn"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-ink transition-colors hover:bg-ink hover:text-paper" aria-label="Close demo">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 px-5 py-3 sm:px-8">
            {STEPS.map((s, i) => (
              <button key={s.title} onClick={() => setStep(i)} data-testid={`demo-mode-step-${i + 1}`} aria-label={s.title}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-terra" : "bg-sand"}`} />
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-5 pb-8 sm:px-8" data-lenis-prevent>
            <div className="mx-auto max-w-5xl pt-4">
              <AnimatePresence mode="wait">
                {!last ? (
                  <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.4 }}>
                    <p className="mb-1 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-terra">
                      {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")}
                    </p>
                    <h2 className="mb-6 font-serif text-2xl text-ink sm:text-3xl">{STEPS[step].title}</h2>
                    <Step />
                  </motion.div>
                ) : (
                  <motion.div key="end" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                    className="flex min-h-[50vh] flex-col items-center justify-center text-center" data-testid="demo-mode-end">
                    <p className="eyebrow">End of walkthrough</p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ink sm:text-4xl">
                      This is how your DMC could work with Travel-Tek.
                    </h2>
                    <p className="mt-4 max-w-md text-base text-stone2">
                      Now imagine it with your destinations, your inventory and your brand.
                    </p>
                    <button
                      onClick={() => { setOpen(false); setTimeout(() => scrollToId("#demo")(), 150); }}
                      data-testid="demo-mode-book-btn"
                      className="mt-8 rounded-full bg-ink px-8 py-4 text-sm font-bold text-paper transition-colors duration-300 hover:bg-terra hover:text-ink"
                    >
                      Book a Personalized Demo
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-sand px-5 py-3.5 sm:px-8">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="demo-mode-prev-btn"
              className="inline-flex items-center gap-2 rounded-full border border-sand px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={() => setStep((s) => Math.min(STEPS.length, s + 1))}
              data-testid="demo-mode-next-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-terra hover:text-ink"
            >
              {last ? "Restart tour" : step === STEPS.length - 1 ? "Finish" : "Next"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
